/**
 * StageFooterEditorBody — the editor for the per-stage calculation footer.
 *
 * Renders a list of calculation rows (function + optional property) with
 * add/remove, plus a short helper line. Rendered inside the "Calculations" tab
 * of the pipeline editor slideout (StagesSlideout).
 *
 * It does NOT render any wrapper or footer buttons — the parent supplies
 * save/reset actions.
 */

import { Plus, Trash01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Select, type SelectItemType } from '@/lib/base/forms/select/select';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

import type { StageFooterDraftResult } from '../hooks/useStageFooterDraft';
import type {
  StageFooterCalculation,
  StageFooterFunction,
} from '../utils/stageFooterConfig';

// ── Module-level constants ───────────────────────────────────────────

const FUNCTION_OPTIONS: SelectItemType[] = [
  { id: 'sum', label: 'Sum' },
  { id: 'avg', label: 'Average' },
  { id: 'count', label: 'Count' },
];

const HELPER_TEXT =
  'Shown at the bottom of each stage. Removing all calculations hides the footer.';

// ── Props ────────────────────────────────────────────────────────────

export interface StageFooterEditorBodyProps {
  draftState: StageFooterDraftResult;
  propertySchemas: Record<string, PropertyTypeSchema>;
  fieldTypeAggregations: Record<string, string[]>;
}

/** Props for the calculations tab in pipeline slideouts. */
export interface StageFooterTabProps extends StageFooterEditorBodyProps {
  onSaveFooterConfig: () => void;
  onResetFooterConfig: () => void;
}

// ── Row ──────────────────────────────────────────────────────────────

interface StageFooterCalculationRowProps {
  index: number;
  calculation: StageFooterCalculation;
  propertyOptions: SelectItemType[];
  onFunctionChange: (index: number, fn: StageFooterFunction) => void;
  onPropertyChange: (index: number, property: string) => void;
  onRemove: (index: number) => void;
}

function StageFooterCalculationRow({
  index,
  calculation,
  propertyOptions,
  onFunctionChange,
  onPropertyChange,
  onRemove,
}: StageFooterCalculationRowProps) {
  const handleFunctionChange = useCallback(
    (key: unknown) => {
      onFunctionChange(index, String(key) as StageFooterFunction);
    },
    [index, onFunctionChange]
  );

  const handlePropertyChange = useCallback(
    (key: unknown) => {
      onPropertyChange(index, String(key));
    },
    [index, onPropertyChange]
  );

  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  const needsProperty = calculation.function !== 'count';

  return (
    <div className='flex items-center gap-2'>
      <div className='w-32 flex-shrink-0'>
        <Select
          size='sm'
          aria-label='Calculation function'
          items={FUNCTION_OPTIONS}
          selectedKey={calculation.function}
          onSelectionChange={handleFunctionChange}
        >
          {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
        </Select>
      </div>

      {needsProperty && (
        <div className='flex-1 min-w-0'>
          <Select
            size='sm'
            aria-label='Calculation property'
            placeholder='Select property'
            items={propertyOptions}
            selectedKey={calculation.property ?? null}
            onSelectionChange={handlePropertyChange}
          >
            {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
          </Select>
        </div>
      )}

      <Button
        color='tertiary'
        size='sm'
        iconLeading={Trash01}
        onClick={handleRemove}
        aria-label='Remove calculation'
        className='flex-shrink-0'
      />
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────

export function StageFooterEditorBody({
  draftState,
  propertySchemas,
  fieldTypeAggregations,
}: StageFooterEditorBodyProps) {
  const {
    draft,
    calcIds,
    addCalculation,
    removeCalculation,
    setCalculationFunction,
    setCalculationProperty,
  } = draftState;

  // Property options eligible for each aggregation function. `sum` and `avg`
  // are computed independently because a field type could support one but not
  // the other.
  const propertyOptionsByFunction = useMemo(() => {
    const build = (fn: StageFooterFunction): SelectItemType[] =>
      Object.values(propertySchemas)
        .filter(schema =>
          (fieldTypeAggregations[schema.field_type_name] ?? []).includes(fn)
        )
        .sort((a, b) =>
          (a.label || a.property_type_name).localeCompare(
            b.label || b.property_type_name
          )
        )
        .map(schema => ({
          id: schema.property_type_name,
          label: schema.label || schema.property_type_name,
        }));
    return { sum: build('sum'), avg: build('avg') };
  }, [propertySchemas, fieldTypeAggregations]);

  const handleFunctionChange = useCallback(
    (index: number, fn: StageFooterFunction) => {
      setCalculationFunction(index, fn);
      if (fn === 'count') {
        return;
      }
      // Ensure a sum/avg row always has a valid property selected.
      const options =
        fn === 'avg'
          ? propertyOptionsByFunction.avg
          : propertyOptionsByFunction.sum;
      const current = draftState.getDraft().calculations[index]?.property;
      const stillValid = current && options.some(o => o.id === current);
      if (!stillValid && options.length > 0) {
        setCalculationProperty(index, options[0].id);
      }
    },
    [
      setCalculationFunction,
      setCalculationProperty,
      propertyOptionsByFunction,
      draftState,
    ]
  );

  const handlePropertyChange = useCallback(
    (index: number, property: string) => {
      setCalculationProperty(index, property);
    },
    [setCalculationProperty]
  );

  const handleRemove = useCallback(
    (index: number) => {
      removeCalculation(index);
    },
    [removeCalculation]
  );

  return (
    <div className='flex flex-col gap-4 px-4 py-3'>
      <p className='text-sm text-[var(--color-text-tertiary)]'>{HELPER_TEXT}</p>

      {draft.calculations.length === 0 ? (
        <p className='py-4 text-sm text-[var(--color-text-tertiary)] text-center'>
          No calculations. The stage footer is hidden.
        </p>
      ) : (
        <div className='space-y-2'>
          {draft.calculations.map((calc, i) => (
            <StageFooterCalculationRow
              key={calcIds[i]}
              index={i}
              calculation={calc}
              propertyOptions={
                calc.function === 'avg'
                  ? propertyOptionsByFunction.avg
                  : propertyOptionsByFunction.sum
              }
              onFunctionChange={handleFunctionChange}
              onPropertyChange={handlePropertyChange}
              onRemove={handleRemove}
            />
          ))}
        </div>
      )}

      <div>
        <Button
          color='link-gray'
          size='sm'
          iconLeading={Plus}
          onClick={addCalculation}
          aria-label='Add calculation'
        >
          Add calculation
        </Button>
      </div>
    </div>
  );
}
